/**
 * The Netxol One system on the sign-in page: Netxol AI in the middle, the ten modules on a ring around it, the ISP's
 * people above (joined to Subscribers) and its network below (joined to Network). Every module has one straight line
 * to the AI; each line carries one pulse at a time — cyan going in (live data, monitoring), then amber coming back
 * (Netxol AI's action, always with approval). Pure SVG + CSS; hidden on small screens with the hero panel.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Bot, Cable, FileBarChart, Landmark, Network, Receipt, Router, ShieldCheck, Store, UserCog, Users, Workflow, type LucideIcon } from 'lucide-react';
import { useT } from '@/i18n';

const W = 640; const H = 620; const CX = 320; const CY = 310; const R = 222;
const CORE_R = 58; const TILE_R = 30;
const DATA_COLOR = '#22d3ee'; const ACTION_COLOR = '#fbbf24';
type Mod = { key: string; icon: LucideIcon; from: string; to: string };
// clockwise from the top: the people side at the top, the network side at the bottom
const MODULES: Mod[] = [
  { key: 'crm', icon: Users, from: '#0ea5e9', to: '#0369a1' },
  { key: 'billing', icon: Receipt, from: '#10b981', to: '#047857' },
  { key: 'erp', icon: Landmark, from: '#14b8a6', to: '#0f766e' },
  { key: 'hr', icon: UserCog, from: '#f43f5e', to: '#be123c' },
  { key: 'routers', icon: Router, from: '#d946ef', to: '#a21caf' },
  { key: 'network', icon: Network, from: '#8b5cf6', to: '#6d28d9' },
  { key: 'aaa', icon: ShieldCheck, from: '#f59e0b', to: '#b45309' },
  { key: 'workflows', icon: Workflow, from: '#6366f1', to: '#4338ca' },
  { key: 'reports', icon: FileBarChart, from: '#06b6d4', to: '#0e7490' },
  { key: 'resellers', icon: Store, from: '#f97316', to: '#c2410c' },
];
const at = (i: number) => {
  const a = -Math.PI / 2 + (i / MODULES.length) * Math.PI * 2;
  return { x: CX + R * Math.cos(a), y: CY + R * Math.sin(a), a };
};

/** A 3D-looking tile: gradient body, top light, darker bottom edge, soft drop shadow. */
function Tile({ icon: Icon, from, to }: { icon: LucideIcon; from: string; to: string }) {
  const style: CSSProperties = {
    width: TILE_R * 2, height: TILE_R * 2, background: `linear-gradient(145deg, ${from}, ${to})`,
    boxShadow: `inset 0 2px 0 rgba(255,255,255,.35), inset 0 -5px 0 rgba(0,0,0,.22), 0 12px 24px -8px ${to}dd, 0 0 0 1px rgba(255,255,255,.08)`,
  };
  return (
    <div className="relative flex items-center justify-center rounded-2xl" style={style}>
      <div className="pointer-events-none absolute inset-x-1.5 top-1 h-1/2 rounded-t-xl bg-gradient-to-b from-white/30 to-transparent" />
      <Icon className="relative size-[46%] text-white drop-shadow-[0_2px_2px_rgba(0,0,0,.35)]" strokeWidth={2.1} />
    </div>
  );
}

function Label({ n, chip }: { n: string; chip?: boolean }) {
  const t = useT();
  return (
    <div className={`my-1.5 flex flex-col items-center ${chip ? 'rounded-md bg-[#0b1530]/85 px-1.5 py-0.5' : ''}`}>
      <div className="whitespace-nowrap text-center text-[11.5px] font-semibold leading-tight text-white">{t(`login.dia.m.${n}`)}</div>
      <div className="whitespace-nowrap text-center text-[10px] leading-tight text-blue-100/55">{t(`login.dia.s.${n}`)}</div>
    </div>
  );
}

export function SystemDiagram() {
  const t = useT();
  const nodes = MODULES.map((m, i) => ({ ...m, ...at(i) }));
  const people = { x: CX, y: 18 }; const field = { x: CX, y: H - 18 };
  const crm = nodes[0]; const net = nodes[5];
  const PERIOD = 6;   // seconds: in, short pause, out, rest
  // drawn at one design size and scaled as a whole, so tiles, labels and lines always keep their places
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / W || 1));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className="relative mx-auto h-full max-h-[720px] max-w-full select-none" style={{ aspectRatio: `${W} / ${H}` }} aria-label={t('login.dia.title')}>
     <div className="absolute left-0 top-0 origin-top-left" style={{ width: W, height: H, transform: `scale(${scale})` }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="absolute inset-0 overflow-visible">
        <defs>
          <radialGradient id="nxCoreGlow"><stop offset="0%" stopColor="#60a5fa" stopOpacity=".45" /><stop offset="65%" stopColor="#7c3aed" stopOpacity=".12" /><stop offset="100%" stopColor="#7c3aed" stopOpacity="0" /></radialGradient>
        </defs>
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(148,163,255,.14)" strokeWidth={1} />
        <circle cx={CX} cy={CY} r={140} fill="url(#nxCoreGlow)" />
        <circle cx={CX} cy={CY} r={CORE_R} fill="none" stroke="#818cf8" strokeOpacity=".45" className="nx-pulse" />

        {/* people → Subscribers, Network → the network: one short straight line each */}
        <line x1={people.x} y1={people.y + 14} x2={crm.x} y2={crm.y - TILE_R - 4} stroke="rgba(165,180,252,.45)" strokeWidth={1.2} strokeDasharray="3 4" />
        <line x1={net.x} y1={net.y + TILE_R + 4} x2={field.x} y2={field.y - 14} stroke="rgba(165,180,252,.45)" strokeWidth={1.2} strokeDasharray="3 4" />

        {/* Netxol AI ↔ each module: a straight line from the core's edge to the tile's edge */}
        {nodes.map((n, i) => {
          const ux = Math.cos(n.a); const uy = Math.sin(n.a);
          const inner = { x: CX + ux * (CORE_R + 6), y: CY + uy * (CORE_R + 6) };
          const outer = { x: n.x - ux * (TILE_R + 6), y: n.y - uy * (TILE_R + 6) };
          const inPath = `M${outer.x.toFixed(1)},${outer.y.toFixed(1)} L${inner.x.toFixed(1)},${inner.y.toFixed(1)}`;
          const outPath = `M${inner.x.toFixed(1)},${inner.y.toFixed(1)} L${outer.x.toFixed(1)},${outer.y.toFixed(1)}`;
          const begin = `${(i * 0.6).toFixed(1)}s`;
          return (
            <g key={n.key}>
              <line x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y} stroke="rgba(148,163,255,.32)" strokeWidth={1.4} />
              {/* in: cyan, first 40 % of the period */}
              <circle r={3.6} fill={DATA_COLOR} style={{ filter: `drop-shadow(0 0 4px ${DATA_COLOR})` }} opacity={0}>
                <animateMotion dur={`${PERIOD}s`} repeatCount="indefinite" begin={begin} path={inPath} keyPoints="0;1;1" keyTimes="0;0.4;1" calcMode="linear" />
                <animate attributeName="opacity" dur={`${PERIOD}s`} repeatCount="indefinite" begin={begin} values="1;1;0;0" keyTimes="0;0.4;0.41;1" />
              </circle>
              {/* out: amber, 50–90 % of the period */}
              <circle r={3.6} fill={ACTION_COLOR} style={{ filter: `drop-shadow(0 0 4px ${ACTION_COLOR})` }} opacity={0}>
                <animateMotion dur={`${PERIOD}s`} repeatCount="indefinite" begin={begin} path={outPath} keyPoints="0;0;1;1" keyTimes="0;0.5;0.9;1" calcMode="linear" />
                <animate attributeName="opacity" dur={`${PERIOD}s`} repeatCount="indefinite" begin={begin} values="0;0;1;1;0;0" keyTimes="0;0.49;0.5;0.9;0.91;1" />
              </circle>
            </g>
          );
        })}
      </svg>

      {/* your people (top), your network (bottom) */}
      {[{ p: people, icon: Users, label: t('login.dia.people'), sub: t('login.dia.peopleSub') }, { p: field, icon: Cable, label: t('login.dia.field'), sub: t('login.dia.fieldSub') }].map(({ p, icon: Icon, label, sub }) => (
        <div key={label} className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-white/15 bg-[#101c3d]/90 px-3.5 py-1.5 shadow-lg shadow-black/20" style={{ left: p.x, top: p.y }}>
          <Icon className="size-3.5 text-sky-300" /><span className="text-[11.5px] font-semibold text-white">{label}</span><span className="text-[10.5px] text-blue-100/60">{sub}</span>
        </div>
      ))}

      {/* the modules: the tile sits on the ring; its label is above it at the top of the ring, below it elsewhere */}
      {nodes.map((n) => {
        const top = Math.sin(n.a) < -0.9; const bottom = Math.sin(n.a) > 0.9;   // their outer side holds the line to people / network
        return (
          <div key={n.key} className="absolute" style={{ left: n.x, top: n.y }}>
            <div className="absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2"><Tile icon={n.icon} from={n.from} to={n.to} /></div>
            <div className={`absolute left-0 -translate-x-1/2 ${bottom ? 'bottom-[32px]' : 'top-[32px]'}`}><Label n={n.key} chip={top || bottom} /></div>
          </div>
        );
      })}

      {/* Netxol AI */}
      <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: CX, top: CY }}>
        <div className="relative flex items-center justify-center rounded-full" style={{ width: CORE_R * 2, height: CORE_R * 2,
          background: 'radial-gradient(circle at 32% 28%, #bfdbfe 0%, #3b82f6 32%, #4f46e5 62%, #3b0764 100%)', boxShadow: '0 0 60px 6px rgba(99,102,241,.5), inset 0 -10px 22px rgba(0,0,0,.35), inset 0 6px 14px rgba(255,255,255,.35)' }}>
          <div className="flex flex-col items-center text-white">
            <Bot className="size-7 drop-shadow" strokeWidth={2} />
            <div className="mt-0.5 text-[13px] font-bold tracking-tight">Netxol AI</div>
            <div className="text-[9px] font-medium text-blue-100/80">{t('login.dia.core')}</div>
          </div>
        </div>
      </div>
     </div>
    </div>
  );
}

/** The legend under the diagram: the same two colours as the pulses. */
export function SystemLegend() {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-blue-100/80">
      <span className="flex items-center gap-2"><span className="size-2.5 rounded-full" style={{ background: DATA_COLOR, boxShadow: `0 0 8px ${DATA_COLOR}` }} />{t('login.dia.monitor')}</span>
      <span className="flex items-center gap-2"><span className="size-2.5 rounded-full" style={{ background: ACTION_COLOR, boxShadow: `0 0 8px ${ACTION_COLOR}` }} />{t('login.dia.act')}</span>
      <span className="flex items-center gap-2"><span className="w-5 border-t border-dashed border-indigo-300/70" />{t('login.dia.edge')}</span>
    </div>
  );
}
